import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  FeatherAlertTriangle,
  FeatherCheck,
  FeatherLoader,
} from "@subframe/core";
import { useNavigate } from "react-router-dom";
import { retryInvoiceProcessingApi } from "../../data/api/documentsApi";
import {
  DocumentEntity,
  DocumentListFilters,
  DocumentType,
} from "../../domain/entities/Document";
import {
  ACTION_CODES,
  MODULE_CODES,
} from "../../../../shared/constants/permissions";
import { usePermissions } from "../../../../shared/presentation/hooks/usePermissions";
import { Alert } from "../../../../ui/components/Alert";
import { Badge } from "../../../../ui/components/Badge";
import { Button } from "../../../../ui/components/Button";
import BulkUploadModal from "../components/BulkUploadModal";
import { DateRangeFilter } from "../../../../shared/presentation/components/DateRangeFilter";
import { DocumentListSection } from "../components/DocumentListSection";
import { DocumentCategory } from "../components/documentFilters";
import StatementUploadModal from "../components/StatementUploadModal";
import StatementReviewDialog from "../components/StatementReviewDialog";
import {
  retryStatementExtractionApi,
  processStatementViaTextractApi,
} from "../../data/api/statementReviewApi";
import {
  getReductoRowState,
} from "../components/reductoStatementState";
import { TransactionReportModal } from "../components/TransactionReportModal";
import { UploadDocumentsSection } from "../components/UploadDocumentsSection";
import { useMinimumLoading } from "../../../../shared/presentation/hooks/useMinimumLoading";
import { usePersistedDateRange } from "../../../../shared/presentation/hooks/usePersistedDateRange";
import { usePersistedState } from "../../../../shared/presentation/hooks/usePersistedState";
import { formatDateForApi } from "../../../../shared/presentation/utils/dateRangeFilters";
import { useDocuments } from "../hooks/useDocuments";
import { useUpload } from "../hooks/useUpload";

const ITEMS_PER_PAGE = 10;
const DOCUMENT_CATEGORIES: DocumentCategory[] = [
  "all",
  "invoice",
  "bill",
  "bank_statement",
  "credit_card",
];
type ListLoadingReason = "initial" | "pagination" | "refresh" | null;

const deserializeString = (raw: string, fallback: string) => {
  const parsed = JSON.parse(raw);
  return typeof parsed === "string" ? parsed : fallback;
};

const deserializeDocumentCategory = (
  raw: string,
  fallback: DocumentCategory,
) => {
  const parsed = JSON.parse(raw);
  return typeof parsed === "string" &&
    DOCUMENT_CATEGORIES.includes(parsed as DocumentCategory)
    ? (parsed as DocumentCategory)
    : fallback;
};

export const DataPage: React.FC = () => {
  const navigate = useNavigate();
  const { hasPermission, loading: permissionsLoading } = usePermissions();

  const canUploadInvoices =
    permissionsLoading ||
    hasPermission(MODULE_CODES.INVOICE, ACTION_CODES.UPLOAD);
  const canUploadBills =
    permissionsLoading || hasPermission(MODULE_CODES.BILL, ACTION_CODES.UPLOAD);
  const canCreateInvoice =
    !permissionsLoading &&
    hasPermission(MODULE_CODES.INVOICE, ACTION_CODES.CREATE);
  const canCreateBill =
    !permissionsLoading &&
    hasPermission(MODULE_CODES.BILL, ACTION_CODES.CREATE);
  const canUploadBankStatements =
    permissionsLoading ||
    hasPermission(MODULE_CODES.BANK_TRANSACTION, ACTION_CODES.CREATE);

  const [showStatementModal, setShowStatementModal] = useState(false);
  const [statementModalType, setStatementModalType] = useState<
    "bank" | "credit"
  >("bank");
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [showTransactionReportModal, setShowTransactionReportModal] =
    useState(false);
  const [selectedDocument, setSelectedDocument] =
    useState<DocumentEntity | null>(null);
  // Statement sitting at awaiting_approval whose review dialog is open
  const [reviewStatementDoc, setReviewStatementDoc] =
    useState<DocumentEntity | null>(null);
  const [dateRange, setDateRange] = usePersistedDateRange(
    "dataUpload.dateRange",
  );
  const [pendingCategory, setPendingCategory] =
    usePersistedState<DocumentCategory>("dataUpload.pending.category", "all", {
      deserialize: deserializeDocumentCategory,
    });
  const [completedCategory, setCompletedCategory] =
    usePersistedState<DocumentCategory>(
      "dataUpload.completed.category",
      "all",
      {
        deserialize: deserializeDocumentCategory,
      },
    );
  const [pendingSearchTerm, setPendingSearchTerm] = usePersistedState(
    "dataUpload.pending.search",
    "",
    { deserialize: deserializeString },
  );
  const [completedSearchTerm, setCompletedSearchTerm] = usePersistedState(
    "dataUpload.completed.search",
    "",
    { deserialize: deserializeString },
  );
  const [pendingLoadingReason, setPendingLoadingReason] =
    useState<ListLoadingReason>("initial");
  const [completedLoadingReason, setCompletedLoadingReason] =
    useState<ListLoadingReason>("initial");
  const [hasLoadedPendingDocuments, setHasLoadedPendingDocuments] =
    useState(false);
  const [hasLoadedCompletedDocuments, setHasLoadedCompletedDocuments] =
    useState(false);
  const [retryingJobId, setRetryingJobId] = useState<string | null>(null);
  // statement_preprocessing id whose Reducto extraction is being re-queued
  const [retryingStatementId, setRetryingStatementId] = useState<
    string | null
  >(null);

  const pendingFilters = useMemo<DocumentListFilters>(
    () => ({
      type: pendingCategory,
      search: pendingSearchTerm,
      dateRange: dateRange.preset === "all_time" ? undefined : dateRange.preset,
      dateFrom:
        dateRange.preset === "all_time"
          ? undefined
          : formatDateForApi(dateRange.from),
      dateTo:
        dateRange.preset === "all_time"
          ? undefined
          : formatDateForApi(dateRange.to),
    }),
    [pendingCategory, pendingSearchTerm, dateRange],
  );

  const completedFilters = useMemo<DocumentListFilters>(
    () => ({
      type: completedCategory,
      search: completedSearchTerm,
      dateRange: dateRange.preset === "all_time" ? undefined : dateRange.preset,
      dateFrom:
        dateRange.preset === "all_time"
          ? undefined
          : formatDateForApi(dateRange.from),
      dateTo:
        dateRange.preset === "all_time"
          ? undefined
          : formatDateForApi(dateRange.to),
    }),
    [completedCategory, completedSearchTerm, dateRange],
  );

  const {
    pendingDocuments,
    pendingPagination,
    pendingLoading,
    fetchPending,
    completedDocuments,
    completedPagination,
    completedLoading,
    fetchCompleted,
    fetchAllDocumentsSilent,
    error: documentsError,
    clearError: clearDocumentsError,
  } = useDocuments(ITEMS_PER_PAGE, pendingFilters, completedFilters);

  const {
    uploadDocuments,
    uploadingBills,
    uploadingInvoices,
    uploadStatus: uploadHookStatus,
    setSuccessStatus,
    clearUploadStatus,
    error: uploadError,
  } = useUpload(() => {
    setPendingLoadingReason("refresh");
    fetchPending(1);
  });

  const billFileInputRef = useRef<HTMLInputElement>(null);
  const invoiceFileInputRef = useRef<HTMLInputElement>(null);
  const autoRefreshIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const pendingLoadingStartedRef = useRef(false);
  const completedLoadingStartedRef = useRef(false);

  useEffect(() => {
    if (!uploadHookStatus.type) return;

    const delay = uploadHookStatus.type === "error" ? 10000 : 5000;
    const timer = setTimeout(() => {
      clearUploadStatus();
    }, delay);

    return () => clearTimeout(timer);
  }, [uploadHookStatus.type, clearUploadStatus]);

  useEffect(() => {
    if (pendingLoading) {
      pendingLoadingStartedRef.current = true;
      return;
    }

    if (pendingLoadingStartedRef.current) {
      pendingLoadingStartedRef.current = false;
      setHasLoadedPendingDocuments(true);
      setPendingLoadingReason(null);
    }
  }, [pendingLoading]);

  useEffect(() => {
    if (completedLoading) {
      completedLoadingStartedRef.current = true;
      return;
    }

    if (completedLoadingStartedRef.current) {
      completedLoadingStartedRef.current = false;
      setHasLoadedCompletedDocuments(true);
      setCompletedLoadingReason(null);
    }
  }, [completedLoading]);

  useEffect(() => {
    const refreshDocuments = async () => {
      try {
        await fetchAllDocumentsSilent(
          pendingPagination.currentPage,
          completedPagination.currentPage,
        );
      } catch (error) {
        console.error("Auto-refresh failed:", error);
      }
    };

    const startInterval = () => {
      if (autoRefreshIntervalRef.current) {
        clearInterval(autoRefreshIntervalRef.current);
      }
      autoRefreshIntervalRef.current = setInterval(refreshDocuments, 30000);
    };

    const stopInterval = () => {
      if (autoRefreshIntervalRef.current) {
        clearInterval(autoRefreshIntervalRef.current);
        autoRefreshIntervalRef.current = null;
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        stopInterval();
        return;
      }

      refreshDocuments();
      startInterval();
    };

    refreshDocuments();
    startInterval();
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      stopInterval();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [
    fetchAllDocumentsSilent,
    pendingPagination.currentPage,
    completedPagination.currentPage,
  ]);

  const handleDocumentClick = (doc: DocumentEntity) => {
    if (doc.documentType === "credit_note") {
      navigate(`/credit/review/${doc.id}`, {
        state: {
          documentId: doc.id,
          signedUrl: doc.s3Url,
          fileName: doc.originalFilename || doc.filename || "credit_note.pdf",
          isManualCreation: false,
        },
      });
      return;
    }

    if (doc.documentType === "debit_note") {
      navigate(`/debit/review/${doc.id}`, {
        state: {
          documentId: doc.id,
          signedUrl: doc.s3Url,
          fileName: doc.originalFilename || doc.filename || "debit_note.pdf",
          isManualCreation: false,
        },
      });
    }
  };

  const handleDateRangeChange = (nextDateRange: typeof dateRange) => {
    if (
      nextDateRange.preset === dateRange.preset &&
      nextDateRange.from?.getTime() === dateRange.from?.getTime() &&
      nextDateRange.to?.getTime() === dateRange.to?.getTime()
    ) {
      return;
    }

    setPendingLoadingReason("refresh");
    setCompletedLoadingReason("refresh");
    setDateRange(nextDateRange);
  };

  const handlePendingCategoryChange = (nextCategory: DocumentCategory) => {
    if (nextCategory === pendingCategory) return;

    setPendingLoadingReason("refresh");
    setPendingCategory(nextCategory);
  };

  const handleCompletedCategoryChange = (nextCategory: DocumentCategory) => {
    if (nextCategory === completedCategory) return;

    setCompletedLoadingReason("refresh");
    setCompletedCategory(nextCategory);
  };

  const handlePendingSearchTermChange = (value: string) => {
    if (value === pendingSearchTerm) return;

    setPendingLoadingReason("refresh");
    setPendingSearchTerm(value);
  };

  const handleCompletedSearchTermChange = (value: string) => {
    if (value === completedSearchTerm) return;

    setCompletedLoadingReason("refresh");
    setCompletedSearchTerm(value);
  };

  const getStatusBadge = (doc: DocumentEntity) => {
    if (doc.isInvoice === 2 && doc.documentType === "invoice") {
      return (
        <Badge variant="error" icon={<FeatherAlertTriangle />}>
          Error: More than 1 Invoice detected.
        </Badge>
      );
    }

    if (doc.isInvoice === 2 && doc.documentType === "bill") {
      return (
        <Badge variant="error" icon={<FeatherAlertTriangle />}>
          Error: More than 1 Bill detected.
        </Badge>
      );
    }

    if (doc.type === "statement") {
      const reductoState = getReductoRowState(doc);

      // ready: no status badge on purpose — the row's Review button says it all
      if (reductoState === "ready") {
        return null;
      }
      if (reductoState === "failed") {
        return (
          <Badge variant="error" icon={<FeatherAlertTriangle />}>
            Failed
          </Badge>
        );
      }
      if (reductoState === "extracting" || reductoState === "stuck") {
        return <Badge variant="neutral">Processing statement</Badge>;
      }
    }

    if (
      doc.type === "statement" &&
      doc.status === "rejected" &&
      !doc.pushedToStatements
    ) {
      return (
        <Badge variant="neutral">Rejected</Badge>
      );
    }

    if (doc.textractStatus === "failed" || doc.status === "failed") {
      const isStatementFailure =
        doc.type === "statement" &&
        doc.status === "failed" &&
        doc.textractStatus !== "failed";
      return (
        <Badge variant="error" icon={<FeatherAlertTriangle />}>
          {isStatementFailure
            ? "Error - Processing Failed. Re-Upload Document."
            : "Error - OCR Failed. Re-Upload Document."}
        </Badge>
      );
    }

    const isCompletedInvoiceOrBill =
      doc.type === "invoice" && doc.status === "completed" && doc.pushedToBills;
    const isCompletedStatement =
      doc.type === "statement" &&
      (doc.status === "uploaded" ||
        doc.status === "completed" ||
        doc.status === "rejected") &&
      doc.textractStatus === "completed" &&
      doc.pushedToStatements;

    if (isCompletedInvoiceOrBill || isCompletedStatement) {
      const summaryParts = getProcessedSummaryParts(doc);
      const hasImportIssues = summaryParts.hasImportIssues;
      const label =
        summaryParts.parts.length > 0
          ? `Completed (${summaryParts.parts.join(", ")})`
          : "Completed";

      return (
        <Badge
          variant={hasImportIssues ? "error" : "success"}
          icon={hasImportIssues ? <FeatherAlertTriangle /> : <FeatherCheck />}
          className="max-w-full overflow-hidden"
        >
          <span className="block w-full truncate text-[11px] leading-4">
            {label}
          </span>
        </Badge>
      );
    }

    return (
      <Badge variant="warning" icon={<FeatherLoader />}>
        Processing
      </Badge>
    );
  };

  const getTypeBadge = (doc: DocumentEntity) => {
    if (doc.type === "statement") {
      if (doc.statementType === "bank") {
        return <Badge variant="success">Bank Statement</Badge>;
      }
      if (doc.statementType === "credit") {
        return <Badge variant="error">Credit Card</Badge>;
      }
      return <Badge>{doc.statementType}</Badge>;
    }

    if (doc.documentType === "bill") {
      return <Badge variant="warning">Bill</Badge>;
    }
    if (doc.documentType === "invoice") {
      return <Badge variant="success">Invoice</Badge>;
    }
    if (doc.documentType === "credit_note") {
      return <Badge variant="success">Credit Note</Badge>;
    }
    if (doc.documentType === "debit_note") {
      return <Badge variant="error">Debit Note</Badge>;
    }

    return <Badge>{doc.documentType}</Badge>;
  };

  const shouldShowRetryButton = (doc: DocumentEntity): boolean => {
    if (doc.documentType !== "bill" && doc.documentType !== "invoice") {
      return false;
    }

    if (doc.retryCount !== undefined && doc.retryCount >= 3) {
      return false;
    }

    if (doc.isInvoice === 2) {
      return true;
    }

    if (doc.textractStatus === "failed" || doc.status === "failed") {
      return true;
    }

    const checkDate = doc.retriedAt
      ? new Date(doc.retriedAt)
      : new Date(doc.createdAt);
    const minutesDiff = (Date.now() - checkDate.getTime()) / (1000 * 60);

    return minutesDiff > 20;
  };

  const getRetryType = (doc: DocumentEntity): "quick" | "full" => {
    if (doc.stageCompleted && doc.isInvoice !== 2) {
      const stageNumber = parseInt(doc.stageCompleted, 10);
      if (!Number.isNaN(stageNumber)) {
        const checkDate = doc.retriedAt
          ? new Date(doc.retriedAt)
          : new Date(doc.createdAt);
        const minutesDiff = (Date.now() - checkDate.getTime()) / (1000 * 60);

        if (minutesDiff > 20) {
          return "full";
        }
      }
    }

    return "quick";
  };

  const handleRetry = async (doc: DocumentEntity) => {
    if (!doc.jobId) return;

    setRetryingJobId(doc.jobId);
    try {
      await retryInvoiceProcessingApi(doc.jobId, getRetryType(doc));
      setSuccessStatus("Processing retry initiated successfully");
      setPendingLoadingReason("refresh");
      setCompletedLoadingReason("refresh");
      fetchPending(1);
      fetchCompleted(1);
    } catch (error: any) {
      setSuccessStatus(error.message || "Failed to retry processing");
    } finally {
      setRetryingJobId(null);
    }
  };

  /**
   * Manual re-run of a failed or stuck Reducto extraction. Separate from
   * handleRetry (which re-runs the invoice/bill pipeline) because this one
   * spends money: it re-queues the paid extraction job.
   */
  const handleRetryReducto = async (doc: DocumentEntity) => {
    setRetryingStatementId(doc.id);
    try {
      await retryStatementExtractionApi(Number(doc.id));
      setSuccessStatus("Extraction re-queued. This usually takes 1 to 4 minutes");
      setPendingLoadingReason("refresh");
      fetchPending(1);
    } catch (error: any) {
      setSuccessStatus(error.message || "Failed to re-queue extraction");
    } finally {
      setRetryingStatementId(null);
    }
  };

  /**
   * Explicit hand-off of a rejected statement to the legacy Textract flow,
   * which extracts AND posts to Zoho automatically. A conscious user action,
   * never a side effect of rejecting.
   */
  const handleTextractStatement = async (doc: DocumentEntity) => {
    setRetryingStatementId(doc.id);
    try {
      await processStatementViaTextractApi(Number(doc.id));
      setSuccessStatus(
        "Statement queued for the standard extraction flow. It will be processed and posted automatically",
      );
      setPendingLoadingReason("refresh");
      fetchPending(1);
    } catch (error: any) {
      setSuccessStatus(error.message || "Failed to queue the standard extraction");
    } finally {
      setRetryingStatementId(null);
    }
  };

  const handleDocumentUploadClick = (type: "bill" | "invoice") => {
    const ref = type === "bill" ? billFileInputRef : invoiceFileInputRef;
    ref.current?.click();
  };

  const handleFileSelection = async (
    event: React.ChangeEvent<HTMLInputElement>,
    docType: DocumentType,
  ) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    try {
      await uploadDocuments(Array.from(files), docType);
    } catch {
      // Upload errors are surfaced through useUpload state.
    } finally {
      const ref =
        docType === "invoice" ? invoiceFileInputRef : billFileInputRef;
      if (ref.current) {
        ref.current.value = "";
      }
    }
  };

  const handleStatementSuccess = (message: string) => {
    setSuccessStatus(message);
    setPendingLoadingReason("refresh");
    fetchPending(1);
  };

  const handleViewReport = (doc: DocumentEntity) => {
    setSelectedDocument(doc);
    setShowTransactionReportModal(true);
  };

  const handlePendingPageChange = (page: number) => {
    if (page >= 1 && page <= pendingPagination.totalPages) {
      if (page === pendingPagination.currentPage) return;

      setPendingLoadingReason("pagination");
      fetchPending(page);
    }
  };

  const handleCompletedPageChange = (page: number) => {
    if (page >= 1 && page <= completedPagination.totalPages) {
      if (page === completedPagination.currentPage) return;

      setCompletedLoadingReason("pagination");
      fetchCompleted(page);
    }
  };

  const handlePendingRefresh = () => {
    setPendingLoadingReason("refresh");
    fetchPending(1);
  };

  const handleCompletedRefresh = () => {
    setCompletedLoadingReason("refresh");
    fetchCompleted(1);
  };

  const error = documentsError || uploadError;
  const pendingListLoading =
    pendingLoading || pendingLoadingReason === "refresh";
  const completedListLoading =
    completedLoading || completedLoadingReason === "refresh";
  const pendingTableLoading =
    (!hasLoadedPendingDocuments && pendingLoadingReason === "initial") ||
    pendingLoadingReason === "pagination" ||
    (pendingLoading && !hasLoadedPendingDocuments);
  const completedTableLoading =
    (!hasLoadedCompletedDocuments && completedLoadingReason === "initial") ||
    completedLoadingReason === "pagination" ||
    (completedLoading && !hasLoadedCompletedDocuments);
  const smoothPendingListLoading = useMinimumLoading(pendingListLoading);
  const smoothCompletedListLoading = useMinimumLoading(completedListLoading);
  const smoothPendingTableLoading = useMinimumLoading(pendingTableLoading);
  const smoothCompletedTableLoading = useMinimumLoading(completedTableLoading);

  if (error && !pendingLoading && !completedLoading) {
    return (
      <div className="flex w-full grow shrink-0 basis-0 flex-col items-center justify-center gap-4 bg-default-background px-8 py-8">
        <div className="text-center">
          <span className="text-body font-body text-error-600">{error}</span>
          <Button
            variant="brand-primary"
            onClick={() => {
              clearDocumentsError();
              setPendingLoadingReason("refresh");
              setCompletedLoadingReason("refresh");
              fetchPending(1);
              fetchCompleted(1);
            }}
            className="mt-4"
          >
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex w-full grow shrink-0 basis-0 flex-col items-start gap-8 bg-default-background px-8 py-8 overflow-auto">
      {uploadHookStatus.type && (
        <Alert
          variant={uploadHookStatus.type}
          title={
            uploadHookStatus.type === "success"
              ? "Upload Successful"
              : "Upload Failed"
          }
        >
          {uploadHookStatus.message}
        </Alert>
      )}

      <UploadDocumentsSection
        filters={
          <DateRangeFilter value={dateRange} onChange={handleDateRangeChange} />
        }
        canCreateInvoice={canCreateInvoice}
        canCreateBill={canCreateBill}
        canUploadInvoices={canUploadInvoices}
        canUploadBills={canUploadBills}
        canUploadBankStatements={canUploadBankStatements}
        uploadingInvoices={uploadingInvoices}
        uploadingBills={uploadingBills}
        onCreateManualInvoice={() => navigate("/invoices/manual/new")}
        onCreateManualBill={() => navigate("/bills/manual/new")}
        onUploadInvoice={() => handleDocumentUploadClick("invoice")}
        onUploadBill={() => handleDocumentUploadClick("bill")}
        onBulkUpload={() => setShowBulkUploadModal(true)}
        onUploadBankStatement={() => {
          setStatementModalType("bank");
          setShowStatementModal(true);
        }}
        onUploadCreditCardBill={() => {
          setStatementModalType("credit");
          setShowStatementModal(true);
        }}
      />

      <DocumentListSection
        title="Pending"
        variant="pending"
        documents={pendingDocuments}
        loading={smoothPendingListLoading}
        tableLoading={smoothPendingTableLoading}
        searchTerm={pendingSearchTerm}
        onSearchTermChange={handlePendingSearchTermChange}
        selectedCategory={pendingCategory}
        onSelectedCategoryChange={handlePendingCategoryChange}
        pagination={pendingPagination}
        itemsPerPage={ITEMS_PER_PAGE}
        retryingJobId={retryingJobId}
        renderTypeBadge={getTypeBadge}
        renderStatusBadge={getStatusBadge}
        shouldShowRetryButton={shouldShowRetryButton}
        onRefresh={handlePendingRefresh}
        onPageChange={handlePendingPageChange}
        onDocumentClick={handleDocumentClick}
        onRetry={handleRetry}
        onViewReport={handleViewReport}
        onReviewStatement={(doc) => setReviewStatementDoc(doc)}
        onRetryReducto={handleRetryReducto}
        onTextractStatement={handleTextractStatement}
        retryingStatementId={retryingStatementId}
      />

      <DocumentListSection
        title="Processed"
        variant="processed"
        documents={completedDocuments}
        loading={smoothCompletedListLoading}
        tableLoading={smoothCompletedTableLoading}
        searchTerm={completedSearchTerm}
        onSearchTermChange={handleCompletedSearchTermChange}
        selectedCategory={completedCategory}
        onSelectedCategoryChange={handleCompletedCategoryChange}
        pagination={completedPagination}
        itemsPerPage={ITEMS_PER_PAGE}
        retryingJobId={retryingJobId}
        renderTypeBadge={getTypeBadge}
        renderStatusBadge={getStatusBadge}
        shouldShowRetryButton={shouldShowRetryButton}
        onRefresh={handleCompletedRefresh}
        onPageChange={handleCompletedPageChange}
        onDocumentClick={handleDocumentClick}
        onRetry={handleRetry}
        onViewReport={handleViewReport}
      />

      <input
        ref={billFileInputRef}
        type="file"
        multiple
        style={{ display: "none" }}
        onChange={(event) => handleFileSelection(event, "bill")}
      />
      <input
        ref={invoiceFileInputRef}
        type="file"
        multiple
        style={{ display: "none" }}
        onChange={(event) => handleFileSelection(event, "invoice")}
      />

      <BulkUploadModal
        open={showBulkUploadModal}
        onOpenChange={setShowBulkUploadModal}
      />

      <StatementUploadModal
        open={showStatementModal}
        onOpenChange={setShowStatementModal}
        type={statementModalType}
        onSuccess={handleStatementSuccess}
      />

      <TransactionReportModal
        open={showTransactionReportModal}
        onOpenChange={setShowTransactionReportModal}
        transactionsJson={selectedDocument?.transactionsJson}
        documentName={
          selectedDocument?.originalFilename || selectedDocument?.filename
        }
      />

      {/* Review-before-push dialog reopened from the pending list */}
      {reviewStatementDoc && (
        <StatementReviewDialog
          open={!!reviewStatementDoc}
          onOpenChange={(isOpen) => {
            if (!isOpen) setReviewStatementDoc(null);
          }}
          jobId={reviewStatementDoc.jobId || ""}
          recordId={Number(reviewStatementDoc.id)}
          filename={
            reviewStatementDoc.originalFilename ||
            reviewStatementDoc.filename ||
            ""
          }
          bankLabel={
            reviewStatementDoc.statementType === "credit"
              ? "Credit card statement"
              : "Bank statement"
          }
          onFinished={handleStatementSuccess}
        />
      )}
    </div>
  );
};

const getProcessedSummaryParts = (doc: DocumentEntity) => {
  let parsedProcessedCount: number | undefined;
  let parsedDuplicateCount: number | undefined;
  let notImportedCount = 0;

  if (doc.transactionsJson) {
    try {
      const parsed = JSON.parse(doc.transactionsJson);
      parsedProcessedCount = parsed.processed?.length;
      parsedDuplicateCount = parsed.duplicate?.length;
      notImportedCount = parsed.not_imported?.length ?? 0;
    } catch {
      // Keep API-provided counts if the statement payload is malformed.
    }
  }

  const processedCount =
    parsedProcessedCount ?? doc.processedTransactionCount ?? 0;
  const duplicateCount =
    parsedDuplicateCount ?? doc.duplicateTransactionCount ?? 0;
  const parts: string[] = [];

  if (processedCount > 0) {
    parts.push(`${processedCount} processed`);
  }
  if (duplicateCount > 0) {
    parts.push(`${duplicateCount} duplicate`);
  }
  if (notImportedCount > 0) {
    parts.push(`${notImportedCount} not imported`);
  }

  return {
    parts,
    hasImportIssues: notImportedCount > 0 || duplicateCount > 0,
  };
};
